import { toast } from "@jammable/ui-core";
import React from "react";
import useAuthModal from "../../hooks/useAuthModal";
import { getIdToken } from "../../utils/firebaseAuth";
import axios from "axios";
import { formatPriceWithLocale, getPriceId } from "../../utils/pricing";
import { Spinner } from "@jammable/ui-components/Loader";
import {
  Credenza,
  CredenzaBody,
  CredenzaContent,
  CredenzaTrigger,
} from "@jammable/ui-components/Dialog";
import { Upgrade } from "@jammable/ui-payments-feature";
import { useRouter } from "next/router";
import { Check } from "lucide-react";
import { useT } from "@jammable/ui-core/src/i18n";
import { liftedVisitor } from "@/utils/lifted";

// Shown names. The plan keys (creator, power) and Stripe stay as they are.
const PLAN_LABEL = { basic: "Basic", creator: "Pro", power: "Studio" };

/**
 * Start a checkout: creates the incomplete subscription and returns the
 * client secret for the Stripe form. PlanCTA's button and the paywall's
 * one-tap reopen (UpgradeModal autoCheckout) both come through here.
 */
export async function startCheckout({
  plan,
  currency,
  billingInterval = "monthly",
  source = "pricing-page",
  offer = null,
}) {
  const interval = billingInterval === "annual" ? "year" : "month";
  let checkoutCurrency = currency;
  let priceId = getPriceId(plan, currency, interval);
  if (plan === "power" && !priceId) {
    checkoutCurrency = "USD";
    priceId = getPriceId(plan, "USD", interval);
  }
  const token = await getIdToken();
  const res = await axios.post(
    `/api/payment/createSubscriptionIntent`,
    {
      priceId,
      currency: checkoutCurrency,
      from: source,
      ...(offer ? { offer, offerPlan: plan } : {}),
      liftedVisitor: liftedVisitor(),
    },
    { headers: { Authorization: token } },
  );
  return res.data.clientSecret;
}

// `t` is optional so callers without a translator keep the English message.
export const checkoutError = (e, t) =>
  e?.response?.status === 409 && e.response.data?.error
    ? e.response.data.error
    : t
      ? t("pricing.error.generic")
      : "Something went wrong. Please contact support!";

/**
 * The plan button on its own: active / manage / upgrade / checkout, chosen
 * from the visitor's current subscription. Shared by the pricing cards and
 * the upgrade modal's single recommended plan so both start exactly the same
 * checkout (createSubscriptionIntent with the same priceId, currency and
 * `from`). Change checkout here, never in a copy.
 */
export const PlanCTA = ({
  plan,
  status,
  currency,
  setClientSecret,
  onSelect,
  billingInterval = "monthly",
  source = "pricing-page",
  onUpgradeClick = null,
  upgradeLoading = false,
  label,
  className = "",
  // "welcome-back": the server swaps in the returning-subscriber price for
  // this plan (lib/welcomeBack).
  offer = null,
}) => {
  const isBasic = plan === "basic";
  const isCreator = plan === "creator";
  const isPower = plan === "power";
  const { checkAuthAndExecute } = useAuthModal();
  const router = useRouter();
  const t = useT();

  const redirectToManageSubscription = async () => {
    const token = await getIdToken();
    axios
      .post(`/api/payment/manageSubscription`, undefined, {
        headers: { Authorization: token },
      })
      .then((res) => (window.location.href = res.data.url))
      .catch(() => toast.error(t("pricing.error.generic")));
  };

  const isActive =
    (isBasic && status === "basic") ||
    (isCreator && status === "creator") ||
    (isPower && status === "power");

  const canUpgrade =
    (status === "basic" && (isCreator || isPower)) ||
    (status === "creator" && isPower);

  const hasHigherPlan =
    (status === "creator" && isBasic) ||
    (status === "power" && (isBasic || isCreator));

  const upgradeTarget = isCreator
    ? "Jammable Creator"
    : isPower
      ? "Jammable Power User"
      : null;

  const base = `tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border-0 tw-px-4 tw-py-4 tw-text-base tw-font-extrabold tw-cursor-pointer tw-transition active:tw-scale-[0.98] disabled:tw-cursor-default ${className}`;

  if (isActive) {
    return (
      <button disabled className={`${base} !tw-bg-jam-lav !tw-text-jam-muted`}>
        <Check size={16} strokeWidth={3} />
        {t("pricing.cta.yourPlan")}
      </button>
    );
  }
  if (hasHigherPlan) {
    return (
      <button onClick={() => redirectToManageSubscription()} className={base}>
        {t("pricing.cta.manage")}
      </button>
    );
  }
  if (canUpgrade && upgradeTarget) {
    const upgradeLabel = t("pricing.cta.upgradeTo", { plan: PLAN_LABEL[plan] });
    if (onUpgradeClick) {
      return (
        <button
          className={base}
          onClick={() => onUpgradeClick(upgradeTarget)}
          disabled={upgradeLoading}
        >
          {upgradeLoading ? (
            <Spinner size={4} className="tw-border-3 tw-border-current" />
          ) : (
            upgradeLabel
          )}
        </button>
      );
    }
    return (
      <Credenza>
        <CredenzaTrigger asChild>
          <button className={base}>{upgradeLabel}</button>
        </CredenzaTrigger>
        <CredenzaContent>
          <CredenzaBody>
            <Upgrade
              type={upgradeTarget}
              fallback={() => redirectToManageSubscription()}
              callback={() => {
                toast.success(t("pricing.cta.updated"));
                setTimeout(() => router.push("/settings"), 1500);
              }}
            />
          </CredenzaBody>
        </CredenzaContent>
      </Credenza>
    );
  }

  return (
    <button
      onClick={async () => {
        if (!checkAuthAndExecute()) return false;
        if (onSelect) onSelect();
        // The checkout opens now, on the promise of the subscription: its
        // form is Stripe's preloaded kind (CreditCheckout `deferred`) and
        // only needs the secret when they pay. If making it fails, back to
        // the plans with the reason.
        const pending = startCheckout({
          plan,
          currency,
          billingInterval,
          source,
          offer,
        });
        setClientSecret(pending);
        pending.catch((e) => {
          toast.error(checkoutError(e, t));
          setClientSecret((current) => (current === pending ? null : current));
        });
      }}
      className={base}
    >
      {label || t("pricing.cta.get", { plan: PLAN_LABEL[plan] })}
    </button>
  );
};

/**
 * One plan card in design language F. `price` is what the first charge is;
 * `originalPrice`, when set, is the standing monthly rate shown struck
 * through (first-month promo). For annual billing pass the yearly price and
 * no originalPrice.
 */
const PricingCard = ({
  plan,
  status,
  currency,
  price,
  originalPrice,
  benefits,
  everythingIn = null,
  setClientSecret,
  onSelect,
  featured = false,
  // Power's card: ink-to-purple, lime button. Reads like `featured` without
  // competing with Creator's purple "Most popular" card.
  dark = false,
  badge = null,
  subtitle = "",
  billingInterval = "monthly",
  source = "pricing-page",
  onUpgradeClick = null,
  upgradeLoading = false,
}) => {
  const isAnnual = billingInterval === "annual";
  const fmt = (n) => formatPriceWithLocale(n, currency);
  const t = useT();

  const priceNote = isAnnual
    ? t("pricing.card.perYear", {
        price: fmt(Math.round((price / 12) * 100) / 100),
      })
    : originalPrice != null
      ? t("pricing.card.firstMonthThen", { price: fmt(originalPrice) })
      : t("pricing.card.perMonth");

  const ctaLabel =
    !isAnnual && originalPrice != null
      ? t("pricing.card.startFor", { price: fmt(price) })
      : t("pricing.cta.get", { plan: PLAN_LABEL[plan] });

  const inverted = featured || dark;
  const ctaClass = inverted
    ? "tw-bg-jam-lime tw-text-jam-ink hover:tw-brightness-95"
    : plan === "basic"
      ? "tw-bg-jam-ink tw-text-white hover:tw-bg-jam-deep"
      : "tw-bg-jam-lav tw-text-jam-ink hover:tw-bg-jam-line";

  return (
    <div
      className={`tw-relative tw-flex tw-h-full tw-flex-col tw-gap-4 ${
        featured
          ? "tw-rounded-[32px] tw-bg-jam-purple tw-p-[22px] tw-text-white md:tw-rounded-[40px] md:tw-p-[30px]"
          : dark
            ? "tw-rounded-[32px] tw-bg-gradient-to-br tw-from-jam-ink tw-to-jam-deep tw-p-[22px] tw-text-white md:tw-rounded-[36px] md:tw-p-7"
            : "tw-rounded-[32px] tw-border-2 tw-border-solid tw-border-jam-line tw-bg-white tw-p-[22px] tw-text-jam-ink md:tw-rounded-[36px] md:tw-p-7"
      }`}
    >
      {badge && (
        <span className="tw-absolute tw--top-3.5 tw-left-[30px] tw-rounded-full tw-bg-jam-lime tw-px-3.5 tw-py-1.5 tw-text-xs tw-font-extrabold tw-text-jam-ink">
          {badge}
        </span>
      )}
      <div>
        <div className="jam-display tw-text-[24px] tw-font-extrabold md:tw-text-[28px]">
          {PLAN_LABEL[plan]}
        </div>
        <div
          className={`tw-text-sm tw-font-semibold ${
            inverted ? "tw-text-[#E3D3F7]" : "tw-text-jam-muted"
          }`}
        >
          {subtitle}
        </div>
      </div>

      <div>
        <div className="tw-flex tw-flex-wrap tw-items-baseline tw-gap-2">
          <span
            className={`jam-display tw-font-extrabold tw-leading-none tw-tracking-[-0.04em] ${
              featured
                ? "tw-text-[44px] md:tw-text-[56px]"
                : "tw-text-[40px] md:tw-text-[52px]"
            }`}
          >
            {fmt(price)}
          </span>
          {!isAnnual && originalPrice != null && (
            <span
              className={`tw-text-base tw-font-bold tw-line-through ${
                inverted ? "tw-text-jam-soft" : "tw-text-jam-muted"
              }`}
            >
              {fmt(originalPrice)}
            </span>
          )}
        </div>
        <div
          className={`tw-mt-1 tw-text-[13px] tw-font-bold ${
            inverted ? "tw-text-[#E3D3F7]" : "tw-text-jam-muted"
          }`}
        >
          {priceNote}
        </div>
      </div>

      <PlanCTA
        plan={plan}
        status={status}
        currency={currency}
        setClientSecret={setClientSecret}
        onSelect={onSelect}
        billingInterval={billingInterval}
        source={source}
        onUpgradeClick={onUpgradeClick}
        upgradeLoading={upgradeLoading}
        label={ctaLabel}
        className={ctaClass}
      />

      <div className="tw-flex tw-flex-col tw-gap-2.5 tw-text-sm tw-font-bold">
        {everythingIn && (
          <span
            className={inverted ? "tw-text-[#E3D3F7]" : "tw-text-jam-muted"}
          >
            {t("pricing.card.everythingIn", { plan: everythingIn })}
          </span>
        )}
        {benefits.map((benefit) => (
          <span key={benefit} className="tw-flex tw-items-start tw-gap-2">
            <Check
              size={16}
              strokeWidth={3}
              className={`tw-mt-0.5 tw-flex-shrink-0 ${
                inverted ? "tw-text-jam-lime" : "tw-text-jam-purple"
              }`}
            />
            {benefit}
          </span>
        ))}
      </div>
    </div>
  );
};

export default PricingCard;
